import { useEffect, useRef, useState } from 'react';
import { addConcernNote, createConcern, ApiError } from '../api.js';
import SymptomInput from './SymptomInput.js';
import SeveritySeg from './SeveritySeg.js';
import { Button, Sheet } from '../ui/index.js';

interface Props {
  /**
   * Existing open concern to append a symptom note to. When null the modal
   * opens a brand-new concern (title required).
   */
  concernId: string | null;
  /** Pre-filled title shown in note mode so the sheet names its concern. */
  concernTitle?: string;
  onClose: () => void;
  /** Called after a successful save (create → concern id, note → concern id). */
  onSaved: () => void;
}

/**
 * Compact "feeling off?" sheet. Two shapes share one form: a create form (title
 * + first symptoms/severity/note) and a note form (symptoms/severity/note added
 * to the newest open concern). Keyboard-usable: Escape and backdrop cancel, the
 * primary action is a submit, focus lands on the first field.
 */
export default function FeelingOffModal({ concernId, concernTitle, onClose, onSaved }: Props): JSX.Element {
  // Note mode can be knocked over into create mode mid-session: if the target
  // concern was closed elsewhere (409), we fall back to opening a new one
  // rather than discarding what the user already typed.
  const [forceCreate, setForceCreate] = useState(false);
  const isNote = concernId !== null && !forceCreate;
  const [title, setTitle] = useState('');
  const [symptoms, setSymptoms] = useState<string[]>([]);
  const [severity, setSeverity] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const firstFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // The sheet handles Escape, the backdrop and focus return; land on the first field.
  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  const canSave = isNote
    ? symptoms.length > 0 || severity !== null || note.trim().length > 0
    : title.trim().length > 0;

  async function save(): Promise<void> {
    if (!canSave || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (isNote) {
        await addConcernNote(concernId!, {
          text: note.trim() || undefined,
          symptoms: symptoms.length > 0 ? symptoms : undefined,
          severity: severity ?? undefined,
        });
      } else {
        await createConcern({
          title: title.trim(),
          note: note.trim() || undefined,
          symptoms: symptoms.length > 0 ? symptoms : undefined,
          severity: severity ?? undefined,
        });
      }
      onSaved();
    } catch (err: unknown) {
      if (isNote && err instanceof ApiError && err.status === 409) {
        // The concern closed out from under us (e.g. another tab/device).
        // Switch to create mode with everything the user already entered
        // intact, rather than losing it to a dead-end error.
        setForceCreate(true);
        setTitle((t) => (t.trim().length > 0 ? t : concernTitle ?? ''));
        setNotice('That concern was closed — this will open a new one.');
        setBusy(false);
        return;
      }
      setError(err instanceof Error ? err.message : 'Could not save. Try again.');
      setBusy(false);
    }
  }

  return (
    <Sheet title={isNote ? 'Log symptom' : 'Feeling off?'} onClose={onClose} className="bc-feel">
      <form
        className="bc-sheetform concern-modal"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {isNote && concernTitle && <p className="bc-feel__on">On “{concernTitle}”</p>}
        {notice && <p className="bc-feel__on bc-warn">{notice}</p>}

        {!isNote && (
          <label className="bc-feel__field">
            <span className="bc-legend">What's going on?</span>
            <input
              ref={firstFieldRef as React.RefObject<HTMLInputElement>}
              className="bc-input"
              placeholder="Sore throat"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
        )}

        <div className="bc-feel__field">
          <span className="bc-legend">Symptoms</span>
          <SymptomInput values={symptoms} onChange={setSymptoms} />
        </div>

        <div className="bc-feel__field">
          <span className="bc-legend">How bad?</span>
          <SeveritySeg value={severity} onChange={setSeverity} />
        </div>

        <label className="bc-feel__field">
          <span className="bc-legend">Note</span>
          <textarea
            ref={isNote ? (firstFieldRef as React.RefObject<HTMLTextAreaElement>) : undefined}
            className="bc-textarea"
            rows={2}
            placeholder="Anything worth noting?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>

        {error && (
          <p className="bc-formerr" role="alert">
            {error}
          </p>
        )}

        <Button type="submit" size="lg" block disabled={!canSave || busy}>
          {busy ? 'Saving…' : isNote ? 'Add note' : 'Open concern'}
        </Button>
      </form>
    </Sheet>
  );
}
